import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { MemberRoleSelect } from './MemberRoleSelect'
import { saveWorkspaceMemberRole } from '../../lib/workspaceMemberRoles'
import { Button } from '../ui/button'
import { TableToolbar, TableFilter, SortableHeader } from '../TableControls'
import { matchesTableSearch, sortTableRows, useTableSort } from '../../lib/tableControls'

interface RosterMember {
  user_id: string
  user_name: string | null
  user_email: string | null
  role: string
  pod_id: string | null
  auth_user_id?: string | null
  can_manage?: boolean
}

const PAGE_SIZE = 8

export function WorkspaceMemberRoster({ members, teams, onViewTeam, teamId, actorRole, onRoleSaved }: {
  members: RosterMember[]
  teams: { id: string; name: string; archived_at?: string | null }[]
  onViewTeam: (id: string) => void
  teamId?: string
  actorRole?: string
  onRoleSaved?: () => void
}) {
  const [search, setSearch] = useState('')
  const [unassignedOnly, setUnassignedOnly] = useState(false)
  const [page, setPage] = useState(0)
  const [roleFilter, setRoleFilter] = useState('all')
  const [teamFilter, setTeamFilter] = useState('all')
  const { sort, setSort, toggleSort: toggle } = useTableSort('name')
  const toggleSort = (key: string) => { toggle(key); setPage(0) }
  const teamNames = useMemo(() => new Map(teams.map(team => [team.id, team.name])), [teams])
  const unassigned = (member: RosterMember) => !member.pod_id || !teamNames.has(member.pod_id)
  const filtered = sortTableRows(members.filter(member => (!unassignedOnly || unassigned(member))
    && (roleFilter === 'all' || member.role === roleFilter)
    && (teamFilter === 'all' || (teamFilter === 'unassigned' ? unassigned(member) : member.pod_id === teamFilter))
    && matchesTableSearch(search, member.user_name, member.user_email)), sort, {
      name: member => member.user_name || member.user_email, role: member => member.role,
      team: member => teamNames.get(member.pod_id || '') || null,
    })
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, pages - 1)
  return <section className="min-w-0 border border-border" aria-label="Workspace members">
    <div className="space-y-3 p-4 sm:p-5">
      <h2 className="text-lg font-semibold">Workspace members</h2>
      <p className="text-sm text-muted-foreground">Everyone added to this workspace, including people without a team assignment.</p>
      <div className="flex flex-wrap gap-2">
        <Button variant={unassignedOnly ? 'outline' : 'default'} aria-pressed={!unassignedOnly} onClick={() => { setUnassignedOnly(false); setPage(0) }}>All members ({members.length})</Button>
        <Button variant={unassignedOnly ? 'default' : 'outline'} aria-pressed={unassignedOnly} onClick={() => { setUnassignedOnly(true); setPage(0) }}>Unassigned ({members.filter(unassigned).length})</Button>
      </div>
      <TableToolbar label="workspace members" query={search} onQueryChange={value => { setSearch(value); setPage(0) }} count={filtered.length} total={members.length}
        active={!!search || unassignedOnly || roleFilter !== 'all' || teamFilter !== 'all'} onClear={() => { setSearch(''); setUnassignedOnly(false); setRoleFilter('all'); setTeamFilter('all'); setSort({ key: 'name', direction: 'asc' }); setPage(0) }}>
        <TableFilter label="Filter workspace role" value={roleFilter} onChange={value => { setRoleFilter(value); setPage(0) }} allLabel="All roles" options={['owner', 'admin', 'manager', 'member'].map(value => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }))} />
        <TableFilter label="Filter workspace team" value={teamFilter} onChange={value => { setTeamFilter(value); setPage(0) }} allLabel="All teams" options={[{ value: 'unassigned', label: 'Unassigned' }, ...teams.map(team => ({ value: team.id, label: team.name }))]} />
      </TableToolbar>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] text-left text-sm">
        <thead className="border-y border-border text-muted-foreground"><tr><SortableHeader className="p-4 font-medium" column="name" label="Member" sort={sort} onSort={toggleSort} /><SortableHeader className="p-4 font-medium" column="role" label="Workspace role" sort={sort} onSort={toggleSort} /><SortableHeader className="p-4 font-medium" column="team" label="Team" sort={sort} onSort={toggleSort} /></tr></thead>
        <tbody>{filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE).map(member => <tr key={member.user_id} className="border-b border-border last:border-b-0">
          <td className="p-4"><p className="font-medium">{member.user_name || member.user_email || 'Unnamed member'}</p><p className="break-all text-muted-foreground">{member.user_email}</p></td>
          <td className="p-4">{teamId ? <MemberRoleSelect role={member.role} name={member.user_name || member.user_email || 'Unnamed member'} actorRole={actorRole}
            managerAvailable={!!member.auth_user_id && member.can_manage !== false && teams.some(team => team.id === member.pod_id && !team.archived_at)}
            onChange={async role => { await saveWorkspaceMemberRole(teamId, { member_user_id: member.user_id }, role); onRoleSaved?.() }} />
            : <span className="capitalize">{member.role}</span>}</td>
          <td className="p-4">{member.pod_id && teamNames.has(member.pod_id)
            ? <Button variant="link" className="h-auto min-h-11 p-0" onClick={() => onViewTeam(member.pod_id!)}>{teamNames.get(member.pod_id)}</Button>
            : <span className="text-muted-foreground">Unassigned</span>}</td>
        </tr>)}</tbody>
      </table>
    </div>
    {filtered.length === 0 && <p className="p-4 text-sm text-muted-foreground">{members.length === 0 ? 'No workspace members yet.' : 'No members match this view.'}</p>}
    {filtered.length > 0 && <div className="flex items-stretch divide-x divide-border border-t border-border">
      <div className="flex-1" />
      <button type="button" disabled={currentPage === 0} aria-label="Previous roster page" onClick={() => setPage(currentPage - 1)}
        className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed">
        <ChevronLeft className={`h-5 w-5 ${currentPage === 0 ? 'text-muted-foreground/40' : 'text-foreground'}`} />
      </button>
      <div className="flex w-40 items-center justify-center py-[18px] text-sm text-muted-foreground">Page {currentPage + 1} of {pages}</div>
      <button type="button" disabled={currentPage + 1 === pages} aria-label="Next roster page" onClick={() => setPage(currentPage + 1)}
        className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed">
        <ChevronRight className={`h-5 w-5 ${currentPage + 1 === pages ? 'text-muted-foreground/40' : 'text-foreground'}`} strokeWidth={2.5} />
      </button>
    </div>}
  </section>
}
